import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState, useEffect } from "react";
import { useCalculationField, useUpdateSavedValues, useVariableData } from "@ploy-lib/calculation";
import SearchIcon from "@material-ui/icons/Search";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import Grid from "@material-ui/core/Grid";
import * as puiFormFields from "@ploy-ui/form-fields";
import * as literalFields from "./literals";
import { DployFormControl, useSelectedField, AriaErrorIdContext, definedProp, makeFieldErrorId } from "@ploy-ui/form-fields";
import { useOptionSource, useServiceHandler, useAlternativeFormik, useDisabledOverride } from "../hooks";
import { FormLabel, FormHelperText, makeStyles } from "@material-ui/core";
import { PendingButton, BisnodeSearchField } from "@ploy-ui/core";
import { Fulfillment, FulfillmentReadOnly, Refinancing, RefinancingReadOnly } from "./Refinancing";
import { CarSummaryField } from "./CarInfoSummary";
import { CommentField } from "./Comment";
import { DriverSelect } from "./DriverSelect";
import { CommisionSection } from "../section/custom";
import { ButtonField, ButtonLink } from "./Buttons/Button";
import { OpenModalButton } from "./Buttons/OpenModalButton";
import { SubmitButton } from "./Buttons/SubmitButton";
import { useServiceHref } from "@ploy-lib/calculation";
import { getIn } from "formik";
import clsx from "clsx";
import { SearchSelectList } from "./SearchSelectList";
import { EquitySlider, TermsYearsSlider, ObjectPriceSlider } from "./Sliders";
import * as customfields from "./customFields";
import { ErrorDisplay } from "@ploy-lib/types";
import { AppActionButton } from "./Buttons/AppActionButton";
import { legacyApiResourceUrl } from "@ploy-lib/core";
import { useIsMountedRef } from "../hooks/useIsMountedRef";
import { MarkdownField } from "./MarkdownField";
import { SeparatorField } from "./SeparatorField";
import { defineMessages, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { NewTabButton } from "./Buttons/NewTabButton";
import { GoToSblButton } from "./Buttons/GoToSblButton";
import { ProductSelector } from "./customFields/ProductSelector";
import { usePageState } from "../PageContext";
import { PropertyOwnersField } from "../section/custom/PropertyOwnersSection";
import { HouseOwnersFromApplicationCustomersField } from "../section/custom/HouseOwnersFromApplicationCustomersSection";
// Lazy-initialized to avoid circular dependency TDZ errors.
// (CalculationField → customFields → LimitedDebtField → barrel → FieldContainer → CalculationField)
let _formFields;
function getFormFields() {
    return (_formFields ??= {
        ...puiFormFields,
        ...customfields,
        AppActionButton,
        ButtonField,
        ButtonLink,
        OpenModalButton,
        SubmitButton,
        Refinancing,
        RefinancingReadOnly,
        Fulfillment,
        FulfillmentReadOnly,
        CarSummaryField,
        CommentField,
        CommissionSummary: CommisionSection,
        BisnodeSearch: BisnodeSearchField,
        SearchSelectList,
        DriverSelect,
        EquitySlider,
        TermsYearsSlider,
        ObjectPriceSlider,
        MarkdownField,
        NewTabButton,
        GoToSblButton,
        ProductSelector,
        SeparatorField,
        PropertyOwnersField,
        HouseOwnersFromApplicationCustomersField
    });
}
let _formLiterals;
function getFormLiterals() {
    return (_formLiterals ??= {
        ...customfields,
        ...literalFields,
        CarSummaryLiteral: CarSummaryField,
        CommentLiteral: CommentField,
        MarkdownLiteral: MarkdownField,
        SeparatorLiteral: SeparatorField
    });
}
const ENTER_KEY = 13;
const serviceErrorMessages = defineMessages({
    generalErrorMessage: {
        id: `error-snackbar.general-error`,
        description: `General snackbar error message`,
        defaultMessage: `An error occured`
    },
    searchButton: {
        id: "template-form.calculation-field.search-button",
        description: "Aria label for the search/save action button next to a calculation field",
        defaultMessage: "Search"
    }
});
const useButtonStyles = makeStyles(() => ({
    iconInLabel: {
        verticalAlign: "bottom"
    }
}));
export function CalculationField(props) {
    const { className, labelComponent, form: formikForm, field: formikField, meta, formTemplateFieldId, fieldName, namespace, literal, disabled: propsDisabled, justifyContent, optionSource, search, save, click, open, renderAs, renderAsLiteral, margin, variant, color, icon, role, saveOnClick, width, suffix, prefix, placeholder, emptyEqualsZero, emptyEqualsZeroHandling, allowNegative, maximumFractionDigits, hasClick, allowEmpty, formatString, boldText, italicText, alwaysEnabled, alwaysDisabled, useFullWidthForHelperText, errorDisplay = ErrorDisplay.Touched, errorDisplayLiteral = ErrorDisplay.Never, modalText, textAlign, mailto, linkIsUrl, disableValidation, action, optionValues, textAreaRows, maxLength, onChange: formikOnChange, onBlur: formikOnBlur, onClick: formikOnClick, pageToOpen, redirectPathKey, redirectFormContext, buttonSize, muiStartIcon, muiEndIcon, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, ...rest } = props;
    const buttonClasses = useButtonStyles();
    const calculation = useCalculationField(namespace, fieldName);
    const { enqueueSnackbar = () => { } } = useSnackbar() || {};
    const intl = useIntl();
    // Avoid loading resources if override has no effect
    const canOverride = !alwaysDisabled && !props.locked && !alwaysEnabled;
    const disabledOverrideState = useDisabledOverride(namespace, canOverride ? fieldName : null);
    const generalErrorMessage = intl.formatMessage(serviceErrorMessages.generalErrorMessage);
    // calculation variables convert NaN to 0 with missing: true
    const variableValue = !emptyEqualsZero && calculation.value === 0 && calculation.missing
        ? NaN
        : calculation.value;
    const classes = useStyles(props);
    const { form, field } = useAlternativeFormik(formikField, formikForm, variableValue);
    const [onSearch, searchPending, result] = useServiceHandler(click, props.field, namespace);
    useEffect(() => {
        if (result && !result.ok)
            enqueueSnackbar(result.error?.name && result.error.message
                ? `${result.error.name}: ${result.error.message}`
                : generalErrorMessage, {
                variant: "error"
            });
    }, [result, enqueueSnackbar, generalErrorMessage]);
    const href = useServiceHref((open && open.namespace) || namespace, open && open.service);
    const { value: pendingItemsFromService } = useVariableData(optionSource && optionSource.pendingFillOptionValues
        ? optionSource.pendingFillOptionValues[0]
        : "", optionSource && optionSource.pendingFillOptionValues
        ? optionSource.pendingFillOptionValues[1]
        : "");
    const optionProps = useOptionSource(field, props.form, namespace, optionSource, onSearch);
    const [saveState, setSaveState] = useState({});
    const isMountedRef = useIsMountedRef();
    const { goto } = usePageState();
    const updateSavedValue = useUpdateSavedValues();
    const onSave = useCallback(async () => {
        try {
            const pristine = calculation.initialFieldValue === field.value;
            const saved = saveState.value === field.value;
            const hasErrors = getIn(props.form.errors, field.name);
            if (pristine || saved || hasErrors || !isMountedRef.current)
                return;
            setSaveState({ loading: true });
            const response = await fetch(legacyApiResourceUrl("AppChange/UpdateFieldWithEvent"), {
                method: "POST",
                body: new Blob([
                    JSON.stringify({
                        value: field.value,
                        field: fieldName
                    })
                ], { type: "application/json" }),
                headers: new Headers({ accept: "application/json" }),
                credentials: "include"
            });
            const data = response.headers.get("content-type")?.includes("json")
                ? await response.json()
                : undefined;
            if (!data.error) {
                updateSavedValue({
                    name: fieldName,
                    namespace,
                    value: field.value
                });
                if (!isMountedRef.current)
                    return;
                setSaveState({ success: true, value: field.value });
            }
        }
        catch (error) {
            if (!isMountedRef.current)
                return;
            setSaveState({ error });
        }
    }, [
        calculation.initialFieldValue,
        field.value,
        field.name,
        saveState.value,
        props.form.errors,
        isMountedRef,
        fieldName,
        updateSavedValue,
        namespace
    ]);
    const { setFieldTouched } = form;
    const searchOnEnter = useCallback(async (e) => {
        const key = e.which || e.keyCode;
        if (key === ENTER_KEY && onSearch) {
            await onSearch();
            setFieldTouched(field.name, true);
        }
    }, [field.name, setFieldTouched, onSearch]);
    const formFields = getFormFields();
    const formLiterals = getFormLiterals();
    let fieldComponent = renderAs && formFields[renderAs] ? renderAs : "TextField";
    // Override TextField with SelectField if select items list is defined
    if ((fieldComponent === "TextField" || fieldComponent === "NumberField") &&
        optionProps &&
        optionProps.items.length > 0)
        fieldComponent = "SelectField";
    // Map field component to literal component
    let literalComponent = renderAsLiteral || fieldComponent.replace("Field", "Literal");
    literalComponent = formLiterals[literalComponent]
        ? literalComponent
        : "TextLiteral";
    let FieldComponent = literal && renderAs !== "AppActionButton"
        ? formLiterals[literalComponent]
        : formFields[fieldComponent];
    const selectedFieldCallback = useSelectedField({
        name: fieldName,
        namespace: namespace
    });
    const onClick = useCallback((e) => {
        if (formikOnClick)
            formikOnClick(e);
        selectedFieldCallback("CLICK");
        if (save && save.type === "button") {
            onSave();
        }
    }, [formikOnClick, onSave, save, selectedFieldCallback]);
    const onChange = useCallback((e) => {
        if (field.onChange) {
            field.onChange(e);
        }
        else if (formikOnChange) {
            formikOnChange(e);
        }
        selectedFieldCallback("CHANGE");
        if (save && saveState.success) {
            setSaveState(s => ({ ...s, success: undefined }));
        }
    }, [field, formikOnChange, save, saveState.success, selectedFieldCallback]);
    const onBlur = useCallback((e) => {
        if (formikOnBlur)
            formikOnBlur(e);
        selectedFieldCallback("BLUR");
    }, [formikOnBlur, selectedFieldCallback]);
    if (!FieldComponent) {
        console.error(`Unknown ${literal ? "literal " : ""}field component`, literal ? literalComponent : fieldComponent, "for field", field, calculation);
        FieldComponent = literal ? formLiterals.TextLiteral : formFields.TextField;
    }
    const errorDisplayToUse = literal ? errorDisplayLiteral : errorDisplay;
    let isButton = fieldComponent.indexOf("Button") !== -1;
    let disabled = propsDisabled ||
        alwaysDisabled ||
        props.locked ||
        ((!calculation.enabled || (literal && !isButton)) && !alwaysEnabled);
    if (disabledOverrideState.disabled !== undefined)
        disabled = disabledOverrideState.disabled;
    const fieldComponentOnBlur = useCallback((e) => {
        if (onBlur)
            onBlur(e);
        if (save && save.type !== "button")
            onSave();
    }, [onBlur, onSave, save]);
    const fieldComponentOnClick = useCallback(async (e) => {
        if (save && save.type !== "button" && onClick)
            onClick(e);
        if (isButton && onSearch) {
            return await onSearch(e, // event
            undefined, // search
            props.eventCode // params
                ? {
                    eventCode: props.eventCode
                }
                : undefined);
        }
        else {
            return null;
        }
    }, [save, onClick, isButton, onSearch, props.eventCode]);
    const errorId = literal ? undefined : makeFieldErrorId(formTemplateFieldId);
    let emptyEqualsZeroOverride = emptyEqualsZero;
    if (emptyEqualsZeroHandling !== undefined) {
        emptyEqualsZeroOverride = emptyEqualsZeroHandling;
    }
    const fieldElement = (_jsx(FieldComponent, { fieldName: {
            name: fieldName,
            namespace: namespace,
            toString: () => `${namespace}.${fieldName}`
        }, ...optionProps, items: optionValues ?? optionProps?.items, className: classes.field, 
        // Forward TemplateField.role as buttonRole — the domain concept
        // (submit, navigate_next, ...) collides with the DOM `role`
        // attribute. Spreading role={undefined} onto non-button fields
        // clobbers MUI defaults like MuiRadioGroup's role="radiogroup".
        buttonRole: role, ...definedProp("aria-labelledby", ariaLabelledBy), ...definedProp("aria-describedby", ariaDescribedBy), label: labelComponent, icon: icon, id: formTemplateFieldId, variant: variant, color: color, multiple: props.multiple, disableClearable: props.disableClearable, row: props.horizontal, manual: props.manual, margin: margin, onKeyPress: searchOnEnter, onBlur: fieldComponentOnBlur, onClick: fieldComponentOnClick, onChange: onChange, goto: goto, disabled: disabled, locked: props.locked, useFullWidthForHelperText: useFullWidthForHelperText, pending: searchPending ||
            pendingItemsFromService === "1" ||
            disabledOverrideState.loading, options: {
            ...rest,
            alwaysEnabled,
            textAreaRows,
            maxLength,
            disableValidation,
            href: rest.href || href
        }, emptyEqualsZero: emptyEqualsZeroOverride, allowNegative: allowNegative, maximumFractionDigits: maximumFractionDigits, field: field, form: form, suffix: suffix === "_" ? "" : suffix, prefix: prefix, clearable: allowEmpty, fullWidth: width !== "auto", saveOnClick: saveOnClick, placeholder: placeholder, formatString: formatString, boldText: boldText, italicText: italicText, modalText: modalText, errorDisplay: errorDisplayToUse, textAlign: textAlign, mailto: mailto, linkIsUrl: linkIsUrl, literal: literal, action: action, pageToOpen: pageToOpen, redirectPathKey: redirectPathKey, redirectFormContext: redirectFormContext, buttonSize: buttonSize, muiStartIcon: muiStartIcon, muiEndIcon: muiEndIcon, hasClick: hasClick }));
    let buttonElement = undefined;
    if (!buttonElement && save && save.type === "button" && !literal) {
        const pristine = calculation.initialFieldValue === field.value;
        const saved = saveState.value === field.value;
        const fieldError = getIn(props.form.errors, field.name);
        buttonElement = (_jsxs(DployFormControl, { variant: variant, margin: margin, error: Boolean(saveState.error), className: classes.saveButtonContainer, children: [labelComponent && _jsx(FormLabel, { children: " " }), _jsx(PendingButton, { pending: saveState.loading, disabled: ((pristine || saveState.success) && !alwaysEnabled) || fieldError, success: saved, error: Boolean(saveState.error), variant: "contained", color: "primary", size: "large", onClick: onClick, className: classes.searchButton, fullWidth: true, hideChildrenWhilePending: true, hideChildrenOnComplete: true, "aria-label": intl.formatMessage(serviceErrorMessages.searchButton), children: _jsx(ArrowForwardIcon, { className: buttonClasses.iconInLabel }) }), saveState.error && (_jsx(FormHelperText, { children: saveState.error.message }))] }));
    }
    const handleMouseDown = (event) => {
        event.preventDefault();
    };
    const buttonElementOnClick = useCallback(async (e) => {
        if (onClick)
            onClick(e);
        if (onSearch)
            await onSearch(e);
        form.setFieldTouched(field.name, true);
    }, [onClick, onSearch, form, field.name]);
    if (!buttonElement && click && !isButton && !literal) {
        buttonElement = (_jsxs(DployFormControl, { variant: variant, margin: margin, className: classes.searchButtonContainer, children: [labelComponent && _jsx(FormLabel, { children: " " }), _jsx(PendingButton, { pending: searchPending, variant: "outlined", size: "large", onClick: buttonElementOnClick, success: false, onMouseDown: handleMouseDown, disabled: disabled, className: classes.searchButton, fullWidth: true, hideChildrenWhilePending: true, "aria-label": intl.formatMessage(serviceErrorMessages.searchButton), children: _jsx(SearchIcon, { className: buttonClasses.iconInLabel }) })] }));
    }
    return (_jsx(AriaErrorIdContext.Provider, { value: errorId, children: _jsx(Grid, { container: true, wrap: "nowrap", justifyContent: justifyContent, className: clsx(className, classes.root), children: fieldElement && buttonElement ? (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, xs: 9, children: fieldElement }), _jsx(Grid, { item: true, xs: 3, children: buttonElement })] })) : fieldElement ? (fieldElement) : buttonElement ? (buttonElement) : null }) }));
}
const useStyles = makeStyles({
    root: {
        alignSelf: "end"
    },
    field: {},
    searchButton: {
        padding: 0,
        minWidth: 0
    },
    searchButtonContainer: {
        width: "100%"
    },
    saveButton: {},
    saveButtonContainer: {
        width: "100%"
    }
}, { name: "DployCalculationField" });
CalculationField.displayName = "DployCalculationField";
export default CalculationField;
//# sourceMappingURL=CalculationField.js.map